import { ConfiguratorData, DensityMapConfigurator } from '.'

// gets a union type of keys from all optional properties in T
type OptionalPropertyOf<T extends object> = Exclude<{
  [K in keyof T]: T extends Record<K, T[K]>
    ? never
    : K
}[keyof T], undefined>

// create a type of required props from all of the optional props in ConfiguratorData
// to ensure we have a fallback value for every optional property
type DefaultData = Required<Pick<ConfiguratorData, OptionalPropertyOf<ConfiguratorData>>>

const simpleClone = <T>(sourceObject: T): T => JSON.parse(JSON.stringify(sourceObject));
const mergeDataWithDefaultValues = (data: ConfiguratorData, defaults: DefaultData) => {
  const merged = {...defaults, ...data};
  return merged as Required<ConfiguratorData>;
};

/**
 * Calculate the absolute Y values for a given layer's base, minHeight and maxHeight.
 * The public API for getLayer style on HeatmapConfig does not require the data argument -- this is bound to the configuration values when the HeatmapConfig object is generated. 
 */
 const getYScale = (data: Required<ConfiguratorData>, layerNumber: number) => {
   const { minHeight, maxHeight, gap, base } = data.scale;
   const baseHeight = base + ((maxHeight + gap) * layerNumber);
   const adjustedMin = baseHeight + minHeight;
   const adjustedMax = baseHeight + maxHeight;
  return {
    base: baseHeight, 
    minHeight: adjustedMin, 
    maxHeight: adjustedMax, 
  };
}

/**
 * The public API for getLayer style on HeatmapConfig does not require the data argument -- this is bound to the configuration values when the HeatmapConfig object is generated. 
 */
const getLayerStyle = (data: ConfiguratorData, layerNumber: number) => simpleClone(layerNumber < data.layerStyles.length ? data.layerStyles[layerNumber] : data.layerStyles[0])

/**
 * Setup default fallback values for any optional props which are not 
 * specified in the application Config data.
 */
const defaults: DefaultData = {
  mapStyle: 'mapbox://styles/mapbox/light-v9?optimize=true',
  camera: {},
  excludeLevels: [],
  maxSpaceDensity: 4,
  autopilotUpdateInterval: 500,
  scale: {
    minHeight: 5,
    maxHeight: 10,
    gap: 0,
    base: 0
  },
}

/**
 * Wraps a set of configuration values in a readonly proxy object and adds some helper functions for data access
 * @param data {ConfiguratorData} Configuration values for the Heatmap views
 * @returns {DensityMapConfigurator} Readonly object with Heatmap configuration values and helper functions
 */
export const getConfigurator = (data: ConfiguratorData) => {
  const mergedData = mergeDataWithDefaultValues(data, defaults);
  
  return new Proxy(mergedData, {
    get: function (target: any, prop: any, receiver: any) {
      switch (prop) {
        case "getLayerStyle":
          return getLayerStyle.bind(null, mergedData);
        case "getYScale":
          return getYScale.bind(null, mergedData);
        default:
          return Reflect.get(target, prop, receiver)
      }
    }, 
    set: (target: any, prop: any, receiver: any) => Reflect.get(target, prop, receiver)
  }) as DensityMapConfigurator;
}